import React, { useEffect, useState } from "react";
import { Dropdown, Input, Button, Table, Segment, Icon } from "semantic-ui-react";
import LocalStorage from "../../../lib/LocalStorage";
import { toast } from "../../common/Toast";
import config from "../../../common/repository/config";
import constants from "../../../common/constants";

const STORAGE_KEY = "diagnosis_form";

const AddDiagnosis: React.FC = () => {
    const [diagnosisType, setDiagnosisType] = useState<string | undefined>();
    const [diagnosis, setDiagnosis] = useState<string>("");
    const [globalRule, setGlobalRule] = useState<string>("");
    const [list, setList] = useState<any[]>([]);
    const [search, setSearch] = useState<string>("");
    const [showForm, setShowForm] = useState<boolean>(false);
    const [editId, setEditId] = useState<string | null>(null);

    const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);

    const fetchList = async () => {
        try {
            if (!token) return;

            const res = await fetch(`${config.apiBaseURL}/diagnosis/list`, {
                method: "POST",
                headers: { "x-access-token": token },
            });

            const data = await res.json();
            setList(Array.isArray(data?.data?.items) ? data.data.items : []);
        } catch {
            toast.error("Failed to load diagnosis list");
            setList([]);
        }
    };

    useEffect(() => {
        if (token) fetchList();
    }, [token]);

    const handleSubmit = async () => {
        if (!diagnosisType) return toast.error("Select diagnosis type");
        if (!diagnosis.trim()) return toast.error("Enter diagnosis");
        if (!token) {
            toast.error('Authentication token is missing.');
            return;
        }
        try {
            const res = await fetch(
                `${config.apiBaseURL}/diagnosis/${editId ? "update" : "add"}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "x-access-token": token,
                    },
                    body: JSON.stringify({
                        id: editId,
                        diagnosisType,
                        diagnosis,
                        globalRule
                    }),
                }
            );

            if (!res.ok) return toast.error("Operation failed");

            toast.success(editId ? "Diagnosis updated" : "Diagnosis added");
            resetForm();
            fetchList();
        } catch {
            toast.error("Something went wrong");
        }
    };

    const openFormWithData = (item: any) => {
        setEditId(item._id);
        setDiagnosisType(item.diagnosis_type);
        setDiagnosis(item.diagnosis);
        setShowForm(true);
    };

    const resetForm = () => {
        setEditId(null);
        setDiagnosis("");
        setDiagnosisType(undefined);
        setShowForm(false);
        localStorage.removeItem(STORAGE_KEY);
    };

    const options = Object.values(constants.diagnosis).map((v) => ({
        key: v,
        text: v,
        value: v,
    }));

    const filteredList = list.filter((item) =>
        `${item.diagnosis_type} ${item.diagnosis}`
            .toLowerCase()
            .includes(search.toLowerCase())
    );

    return (
       <div style={container}>
  <div style={card}>

    {/* ===== Form ===== */}
    {showForm && (
      <Segment>
        <h4>{editId ? "Edit Diagnosis" : "Add Diagnosis"}</h4>

        <Dropdown
          fluid
          selection
          search
          clearable
          placeholder="Select Diagnosis Type"
          options={options}
          value={diagnosisType}
          onChange={(_, d) => setDiagnosisType(d.value as string)}
        />

        <Input
          fluid
          placeholder="Enter diagnosis"
          value={diagnosis}
          onChange={(e) => setDiagnosis(e.target.value)}
          style={{ margin: "15px 0" }}
        />
          <Input
          fluid
          placeholder="Global Rule"
          value={globalRule}
          onChange={(e) => setGlobalRule(e.target.value)}
          style={{ margin: "15px 0" }}
        />

        <Button primary fluid onClick={handleSubmit}>
          {editId ? "Update" : "Submit"}
        </Button>

        <Button
          fluid
          basic
          style={{ marginTop: 8 }}
          onClick={resetForm}
        >
          Cancel
        </Button>
      </Segment>
    )}

    {/* ===== Header + Listing (ONLY WHEN FORM CLOSED) ===== */}
    {!showForm && (
      <>
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "15px",
          }}
        >
          <Button primary onClick={() => setShowForm(true)}>
            <Icon name="plus" />
            Add Diagnosis
          </Button>

          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <Input
              icon="search"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <Button
              basic
              disabled={!search}
              onClick={() => setSearch("")}
            >
              Clear
            </Button>
          </div>
        </div>

        {/* Table */}
        <Table celled compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Type</Table.HeaderCell>
              <Table.HeaderCell>Diagnosis</Table.HeaderCell>
              <Table.HeaderCell>GlobalRule</Table.HeaderCell>
              <Table.HeaderCell width={2}>Action</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {filteredList.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={3} textAlign="center">
                  No records found
                </Table.Cell>
              </Table.Row>
            ) : (
              filteredList.map((item, i) => (
                <Table.Row key={i}>
                  <Table.Cell>{item.diagnosis_type}</Table.Cell>
                  <Table.Cell>{item.diagnosis}</Table.Cell>
                  <Table.Cell>{item.global_rule}</Table.Cell>
                  <Table.Cell>
                    <Button
                      size="tiny"
                      onClick={() => openFormWithData(item)}
                    >
                      Open
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table>
      </>
    )}
  </div>
</div>

    );
};

/* ---------- Styles ---------- */
const container: React.CSSProperties = {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "#f4f6f8",
};

const card: React.CSSProperties = {
    width: "650px",
    padding: "25px",
    background: "#fff",
    borderRadius: "10px",
    boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
};

export default AddDiagnosis;
